import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import PrimaryFilterHelper from '../../../components/primaryFilterHelper';
import LocationFilterDropdown from '../../../components/locationFilterDropdown';
import DateRangePicker from '../../../components/dateRangePicker';
import { useRevenueLeakageSummary } from '../hooks/useRevenueLeakage';
import {
  KpiStrip,
  LeakSkeleton,
  LocationTable,
  SignalTable
} from '../components/LeakageDashboard';

export default function RevenueLeakageDashboard() {
  const companyDetails = useSelector(
    (state: RootState) => state.company?.company_details as any
  );
  const isMaintenance = companyDetails?.maintenance_mode?.enabled;
  const status = companyDetails?.revenue_leakage_status as 'enabled' | 'disabled' | undefined;

  if (status !== 'enabled') {
    return (
      <LayoutWrapper $maintenance={!!isMaintenance}>
        <Unavailable>
          <h1>Revenue Leakage</h1>
          <p>
            Revenue Leakage is available on demo accounts while we finish the feature.
          </p>
        </Unavailable>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper $maintenance={!!isMaintenance}>
      <PrimaryFilterHelper>
        <ActiveDashboard />
      </PrimaryFilterHelper>
    </LayoutWrapper>
  );
}

const ActiveDashboard = () => {
  const companyLocations = useSelector((state: RootState) => state.locations);
  const { data, error, isLoading, isValidating } = useRevenueLeakageSummary();
  const loading = !data && (isLoading || isValidating);
  const currency = data?.currency_code || '';

  return (
    <>
      <Header>
        <div>
          <h1>Revenue Leakage</h1>
          <Sub>See where your restaurants are losing money</Sub>
        </div>
        <Controls>
          {companyLocations.length > 0 && <LocationFilterDropdown />}
          <DateRangePicker />
        </Controls>
      </Header>

      <Content>
        {loading && <LeakSkeleton />}
        {!loading && error && (
          <ErrorBox>
            We could not load this page right now. Please try again in a moment.
            {error?.response?.status === 404 && ' This feature may not be turned on yet.'}
            {error?.response?.status === 423 && ' You may not have permission to view this.'}
          </ErrorBox>
        )}
        {!loading && !error && data && (
          <>
            <KpiStrip kpis={data.kpis} currency={currency} />

            <SectionTitle>Where the money went</SectionTitle>
            <SignalTable signals={data.signals} currency={currency} />

            <SectionTitle>By branch</SectionTitle>
            <LocationTable locations={data.locations} currency={currency} />
          </>
        )}
      </Content>
    </>
  );
};

const LayoutWrapper = styled.div<{ $maintenance: boolean }>`
  background-color: ${({ theme }) => theme.body};
  margin-top: ${({ $maintenance }) => ($maintenance ? '180px' : '125px')};
  padding: 0 32px 48px;
  min-height: calc(100vh - 125px);
  transition: all 0.25s linear 0s;

  @media (max-width: 720px) {
    padding: 0 16px 32px;
    margin-top: ${({ $maintenance }) => ($maintenance ? '150px' : '100px')};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 24px 0 12px;

  h1 {
    font-weight: 800;
    margin: 0;
    color: ${({ theme }) => theme.text};
  }
`;

const Sub = styled.div`
  margin-top: 4px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_muted || theme.text_secondary || theme.text};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Content = styled.div`
  min-height: 240px;
`;

const SectionTitle = styled.h2`
  margin: 22px 0 10px;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const ErrorBox = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(225, 29, 72, 0.25);
  background: rgba(225, 29, 72, 0.06);
  color: ${({ theme }) => theme.text};
`;

const Unavailable = styled.div`
  padding: 48px 16px;
  max-width: 560px;
  h1 {
    font-weight: 800;
    color: ${({ theme }) => theme.text};
  }
  p {
    color: ${({ theme }) => theme.text_secondary || theme.text};
  }
`;
